import ButtonNavLink from "@/lib/components/interactions/buttons/ButtonNavLink";
import {
  ButtonIcon,
  ColorSchemeToggle,
  FlexContainer,
  IconLinkCounter,
  LanguageToggle,
  NavbarButtonsContainer,
  NavbarLogo,
  NavbarMenu,
  NavbarPropsType,
  useScreenWidth,
} from "@/lib/main";
import { CSSProperties, useEffect } from "react";
import LogoutLink from "./LogoutLink";
import NavbarSearchBar from "./NavbarSearchBar";

export default function Navbar({
  breakpoint,

  isDrawerOpen,
  setIsDrawerOpen,

  addNotificationIconLink,
  notificationIconLinkPath,
  notificationIconLinkCount,

  addRegisterButton,
  registerButtonColor,
  registerButtonPath,
  registerButtonLabel,

  addLoginButton,
  loginButtonColor,
  loginButtonPath,
  loginButtonLabel,

  addLogoutLink,
  logoutLinkColor,
  logoutPath,
  logoutLinkText,

  navbarMenuItems,
  isHashLinks,

  addNavbarLogo,
  customNavbarLogo,
  navbarLogoPath,

  addColorSchemeToggle,
  addLanguageToggle,

  addShoppingCart,
  shoppingCartLink,
  shoppingCartCount,

  addSearchBar,
  onSearchSubmit,
  searchBarPlaceholder,
}: NavbarPropsType) {
  const { screenWidth } = useScreenWidth();

  const handleClickBurgerMenu = () => setIsDrawerOpen(!isDrawerOpen);

  useEffect(() => {
    const handleResize = () => {
      if (screenWidth >= breakpoint && isDrawerOpen) setIsDrawerOpen(false);
    };

    window.addEventListener("resize", handleResize);
    // * Initial check
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, [screenWidth, breakpoint, isDrawerOpen, setIsDrawerOpen]);

  const navbarContainerStyle: CSSProperties = {
    position: "relative",
    zIndex: "var(--z-index-header-navbar)",

    backgroundColor: "var(--color-background-navbar)",

    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "var(--spacing-lg)",

    height: "var(--navbar-height)",
    width: "100%",
    maxWidth: "var(--page-container-max-width)",

    padding: "0 var(--page-padding-horizontal)",
    margin: "0 auto",
  };

  const innerContainerStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
  };

  const burgerButtonContainerStyle: CSSProperties = {
    position: "relative",
    zIndex: "var(--z-index-header-navbar-buttons)",
    display: "flex",
    alignItems: "center",
  };

  const logoContainerStyle: CSSProperties = {
    position: "relative",
    zIndex: "var(--z-index-header-navbar-buttons)",
  };

  return (
    <div style={navbarContainerStyle}>
      <div style={innerContainerStyle}>
        <div style={logoContainerStyle}>
          <NavbarLogo
            addNavbarLogo={addNavbarLogo}
            customNavbarLogo={customNavbarLogo}
            setIsDrawerOpen={setIsDrawerOpen}
            to={navbarLogoPath}
          />
        </div>

        {screenWidth >= breakpoint ? (
          <NavbarMenu
            isHashLinks={isHashLinks}
            navbarMenuItems={navbarMenuItems}
          />
        ) : null}
      </div>

      {addSearchBar && screenWidth >= breakpoint ? (
        <NavbarSearchBar
          onSearchSubmit={onSearchSubmit}
          searchBarPlaceholder={searchBarPlaceholder}
        />
      ) : null}

      {screenWidth >= breakpoint ? (
        <NavbarButtonsContainer>
          {addLanguageToggle ? <LanguageToggle /> : null}
          {addColorSchemeToggle ? <ColorSchemeToggle /> : null}

          <FlexContainer gap="lg">
            {addRegisterButton ? (
              <ButtonNavLink
                to={registerButtonPath || "/register"}
                color={registerButtonColor || "brand"}
                size="sm"
              >
                {registerButtonLabel || "Register"}
              </ButtonNavLink>
            ) : null}

            {addLoginButton ? (
              <ButtonNavLink
                to={loginButtonPath || "/login"}
                color={loginButtonColor || "outlined"}
                size="sm"
              >
                {loginButtonLabel || "Login"}
              </ButtonNavLink>
            ) : null}

            {addShoppingCart ? (
              <IconLinkCounter
                count={shoppingCartCount}
                iconColor="secondary"
                iconName="shopping-cart"
                linkTitle={{ ar: "سلة المشتريات", en: "Shopping Cart" }}
                to={shoppingCartLink || "/cart"}
                style={{ padding: "0.5rem" }}
              />
            ) : null}

            {addNotificationIconLink ? (
              <IconLinkCounter
                count={notificationIconLinkCount}
                iconColor="secondary"
                iconName="bell"
                linkTitle={{
                  ar: "الإشعارات",
                  en: "Notifications",
                }}
                to={notificationIconLinkPath || "/notifications"}
                style={{ padding: "0.5rem" }}
              />
            ) : null}
          </FlexContainer>

          {addLogoutLink ? (
            <LogoutLink
              logoutLinkColor={logoutLinkColor}
              logoutLinkText={logoutLinkText}
              logoutPath={logoutPath}
            />
          ) : null}
        </NavbarButtonsContainer>
      ) : (
        <div style={burgerButtonContainerStyle}>
          {addShoppingCart ? (
            <IconLinkCounter
              count={shoppingCartCount}
              iconColor="secondary"
              iconName="shopping-cart"
              linkTitle={{ ar: "سلة المشتريات", en: "Shopping Cart" }}
              to={shoppingCartLink || "/cart"}
              style={{ padding: "0.5rem" }}
            />
          ) : null}
          <ButtonIcon
            onClick={handleClickBurgerMenu}
            iconName={isDrawerOpen ? "close" : "menu"}
          />
        </div>
      )}
    </div>
  );
}
